import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { logger } from '@/lib/logger';
import type { GeoLocationResponseT } from '@/api/geolocation';

type CustomerLocationT = {
  country: string | null;
  /** The country's subdivision code (a US state, like `CA`), when known. */
  region: string | null;
  loading: boolean;
};

const CustomerLocationContext = createContext<CustomerLocationT>({
  country: null,
  region: null,
  loading: false,
});

export const CustomerLocationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [country, setCountry] = useState<string | null>(null);
  const [region, setRegion] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      try {
        const res = await fetch('/api/geolocation', {
          signal: controller.signal,
        });

        if (!res.ok) {
          throw new Error(`Request failed with status ${res.status}`);
        }

        const data: GeoLocationResponseT = await res.json();

        setCountry(data.country ? data.country.toUpperCase() : null);
        setRegion(data.region ? data.region.toUpperCase() : null);
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          logger.info({ err }, 'Could not resolve the visitor country');
        }
      } finally {
        setLoading(false);
      }
    };

    load();

    return () => controller.abort();
  }, []);

  const value = useMemo(
    () => ({ country, region, loading }),
    [country, region, loading],
  );

  return (
    <CustomerLocationContext.Provider value={value}>
      {children}
    </CustomerLocationContext.Provider>
  );
};

export const useCustomerLocation = () => useContext(CustomerLocationContext);
